import { Ban, CheckCircle2, ClipboardCheck, ClipboardList, Clock, Play } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { surveyQuestionnairesApi } from '../surveyApi';
import { SURVEY_STATUS, type Survey, type SurveyQuestionnaire } from '../surveyTypes';
import { SURVEY_ACTS, type SurveyAct } from '../utils/surveyActs';
import { nextAction, type ConfigurationKey, type TabKey } from '../utils/surveyNextAction';
import { SurveyPlanCreateModal } from './SurveyPlanCreateModal';
import { SurveyQuestionnaireModal } from './SurveyQuestionnaireModal';

type Destination = ReturnType<typeof nextAction>;

/**
 * The survey's banner, on the STANDARD `<NextActionAlert>` (product call, 13 Oct 2026) — the NEXT THING TO DO with it, and the way to do it (product call,
 * 13 Oct 2026). The order is `nextAction`'s, the one the page lands by, so the banner and the
 * landing tab cannot disagree about what comes next.
 *
 * Three kinds of action, by what the click commits:
 * - one that OPENS A FORM (create the questionnaire, create a development plan) opens it right
 *   here, whatever tab the reader is on (product call, 13 Oct 2026). No confirmation of its own:
 *   the form's tick is the gate. Each on the key of its own write.
 * - one that MOVES the survey (start, end, re-open) is done HERE and CONFIRMS first, with the
 *   consequence stated — the banner is outside a form, and the record has moved by the time the
 *   dialog closes. On `surveys.update`; without the key the sentence stays and the button goes.
 * - ASSESSING is work on a response the reader has to choose, so that one takes them to the tab, and is NOT DRAWN while they are
 *   already on it: a button that navigates to where you are standing does nothing.
 */
export function SurveyNextActionBanner({
  survey,
  at,
  onGo,
  onChanged,
  onSaved,
}: {
  survey: Survey;
  /** Where the reader is now. */
  at: { tab: TabKey; configuration: ConfigurationKey };
  onGo: (next: Destination) => void;
  onChanged: (next: Survey) => void;
  /** After a form opened from here saved — the page re-reads its counts and its open panel. */
  onSaved: () => void;
}) {
  const toast = useToast();
  const [pending, setPending] = useState<SurveyAct | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [form, setForm] = useState<'questionnaire' | 'plan' | null>(null);

  /*
    A survey holds ONE questionnaire, so "no question yet" is two cases: none at all (create it),
    or one with nothing on it (add to THAT one — a second is refused). Asked only while it matters;
    a failed read offers Create, and the server stays the authority on the rule.
  */
  const needsQuestions = survey.isActive && survey.questionCount === 0;
  // Bumped after a save from here, so a questionnaire saved with no question is found next time.
  const [asked, setAsked] = useState(0);
  const [existing, setExisting] = useState<{ surveyId: string; questionnaire: SurveyQuestionnaire | null } | null>(null);

  useEffect(() => {
    if (!needsQuestions) return;

    const controller = new AbortController();

    surveyQuestionnairesApi
      .list(survey.id, { pageSize: 1, status: 'active' }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setExisting({ surveyId: survey.id, questionnaire: page.items[0] ?? null });
      })
      .catch(() => {
        if (!controller.signal.aborted) setExisting({ surveyId: survey.id, questionnaire: null });
      });

    return () => controller.abort();
  }, [needsQuestions, survey.id, asked]);

  const questionnaire = existing?.surveyId === survey.id ? existing.questionnaire : undefined;

  async function run() {
    if (!pending) return;

    setRunning(true);
    setError(null);

    try {
      const next = await SURVEY_ACTS[pending].run(survey.id);

      toast.success(SURVEY_ACTS[pending].done);
      setPending(null);
      onChanged(next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const destination = nextAction(survey);

  // The Responses tab has views of its own the page cannot see, so "there" is the tab.
  const there =
    at.tab === destination.tab && (destination.tab !== 'configuration' || at.configuration === destination.configuration);

  const unassessed = survey.respondentCount - survey.assessedCount;

  /** One state of the banner: the standard title over a description, and the ONE action it offers. */
  let banner: {
    
    icon: ReactNode;
    title: string;
    description: string;
    actionLabel?: string;
    onAction?: () => void;
    actionPermission?: string;
  };

  if (!survey.isActive) {
    banner = {
      
      icon: <Ban className="h-4 w-4" aria-hidden="true" />,
      title: 'Deleted',
      description: 'Nothing is erased, and it can be restored from the Survey list.',
    };
  } else if (needsQuestions) {
    banner = {
      
      icon: <ClipboardList className="h-4 w-4" aria-hidden="true" />,
      title: 'Build the questionnaire',
      description: 'This survey has no question to answer yet.',
      // Until the read answers, the button cannot know which of the two it opens, so it is not drawn.
      actionLabel: questionnaire === undefined ? undefined : questionnaire ? 'Add questions' : 'Create questionnaire',
      onAction: () => setForm('questionnaire'),
      actionPermission: 'surveys.update',
    };
  } else if (survey.status === SURVEY_STATUS.NotStarted) {
    banner = {
      
      icon: <Play className="h-4 w-4" aria-hidden="true" />,
      title: 'Start the survey',
      description: 'Its questionnaire is built, and it cannot be answered until the survey is started.',
      actionLabel: SURVEY_ACTS.start.title,
      onAction: () => setPending('start'),
      actionPermission: 'surveys.update',
    };
  } else if (unassessed > 0) {
    banner = {
      
      icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
      title: 'Assess the responses',
      description: `${unassessed} of ${survey.respondentCount} ${survey.respondentCount === 1 ? 'response is' : 'responses are'} not assessed yet.`,
      // Not drawn while the reader is already there: a button to where you are standing does nothing.
      actionLabel: there ? undefined : 'Assess responses',
      onAction: () => onGo(destination),
      actionPermission: 'surveys.read',
    };
  } else if (survey.assessedCount > 0) {
    banner = {
      
      icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
      title: 'Create the development plan',
      description: 'Every response is assessed, so the assessed employees can be planned for.',
      actionLabel: 'Create development plan',
      onAction: () => setForm('plan'),
      actionPermission: 'surveys.plan',
    };
  } else if (survey.status === SURVEY_STATUS.Started) {
    banner = {
      
      icon: <Clock className="h-4 w-4" aria-hidden="true" />,
      title: 'Waiting for answers',
      description: 'The survey is started and nobody has answered it yet. End it when it should stop taking answers.',
      actionLabel: SURVEY_ACTS.end.title,
      onAction: () => setPending('end'),
      actionPermission: 'surveys.update',
    };
  } else {
    banner = {
      
      icon: <Clock className="h-4 w-4" aria-hidden="true" />,
      title: 'Ended with no response',
      description: 'Re-open the survey to take answers again.',
      actionLabel: SURVEY_ACTS.reopen.title,
      onAction: () => setPending('reopen'),
      actionPermission: 'surveys.update',
    };
  }

  return (
    <>
      {/* THE STANDARD banner for a profile with a suggested action — `<NextActionAlert>`, its own CTA. */}
      <NextActionAlert id="survey-profile-banner" {...banner} />

      {form === 'questionnaire' ? (
        <SurveyQuestionnaireModal
          open
          surveyId={survey.id}
          editing={questionnaire ?? null}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            setExisting(null);
            setAsked((n) => n + 1);
            onSaved();
          }}
        />
      ) : null}

      {form === 'plan' ? (
        <SurveyPlanCreateModal
          open
          surveyId={survey.id}
          onClose={() => setForm(null)}
          onCreated={() => {
            setForm(null);
            onSaved();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={() => void run()}
          title={SURVEY_ACTS[pending].title}
          requireConfirm
          loading={running}
          id={`survey-banner-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>{SURVEY_ACTS[pending].consequence}</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}
